import ExifReader from 'exifreader';
import { action, computed, flow, makeObservable, observable } from 'mobx';

import { requireService } from 'src/packages/di';

import type { Broadcast, WellboreApi } from '../api/Wellbore.api';

import {
  extractScreencastTooltipsChunk,
  parseScreencastTooltips,
} from './screencast-tooltips/screencastTooltipsParser';
import type { ScreencastTooltips } from './screencast-tooltips/types';

export class ImageBroadcastChannel {
  @observable inProgress = false;

  @observable info: Broadcast | null = null;

  @observable subscribedBroadcast = false;

  id: number;

  @observable image: string | null = null;

  @observable screencastTooltips: ScreencastTooltips | null = null;

  constructor(
    id: number,
    private wellDirectoryService: WellboreApi,
    private wellStatusService = requireService('wellStatusService'),
    private notifications = requireService('notifications'),
  ) {
    this.id = id;
    makeObservable(this);
  }

  @computed
  get comments() {
    return [...(this.screencastTooltips?.comments ?? [])].sort((a, b) => a.title.localeCompare(b.title));
  }

  @action.bound
  subscribe() {
    if (this.subscribedBroadcast) {
      return;
    }
    this.wellStatusService.subscribe(`/broadcast/state/broadcast/${this.id}`, this.update);
    this.subscribedBroadcast = true;
  }

  @action.bound
  unsubscribe() {
    this.wellStatusService.unsubscribe(`/broadcast/state/broadcast/${this.id}`, this.update);
    this.subscribedBroadcast = false;
  }

  @flow.bound
  async *update() {
    this.inProgress = true;
    try {
      const data = await this.wellDirectoryService.getBroadcastInfo(this.id);
      await this.onImageUpdate();
      yield;
      this.info = data;
    } catch (e) {
      yield;
      console.error(e);
      this.notifications.showErrorMessageT('imageBroadcast:error.broadcastInfo');
    } finally {
      this.inProgress = false;
    }
  }

  @flow.bound
  async *onImageUpdate() {
    try {
      const data = await this.wellDirectoryService.getBroadcastImage(this.id);
      const tags = await ExifReader.load(getPngBinaryData(data), {
        expanded: true,
        async: true,
        includeTags: { png: true },
      });

      yield;
      const rawTooltips = extractScreencastTooltipsChunk(tags);
      this.screencastTooltips = null;

      if (rawTooltips) {
        const parsedTooltips = parseScreencastTooltips(rawTooltips);
        if (parsedTooltips.data) {
          this.screencastTooltips = parsedTooltips.data;
        }
      }

      this.image = data;
    } catch (e) {
      yield;
      console.error(e);
      this.notifications.showErrorMessageT('imageBroadcast:error.broadcastInfo');
    }
  }
}

function getPngBinaryData(data: string): ArrayBuffer {
  const base64 = data.includes(',') ? data.slice(data.indexOf(',') + 1) : data;
  const binaryString = window.atob(base64);
  const bytes = new Uint8Array(binaryString.length);

  for (let i = 0; i < binaryString.length; i += 1) {
    bytes[i] = binaryString.charCodeAt(i);
  }

  return bytes.buffer;
}
